import { useState } from 'react';
import { getFlagVariationIndexFromId } from '@gonfalon/flag-variations/getFlagVariationIndexFromId';
import { VariationDisplay } from '@gonfalon/flag-variations/VariationDisplay';
import { getDisplayDurationFromMs } from '@gonfalon/format/getDisplayDurationFromMs';
import { Cell, CellLayoutValue, Grid, GridLayoutValue } from '@gonfalon/ui/grid';
import { Button, Code, Tag, TagGroup, TagList } from '@launchpad-ui/components';

import type { Variation as FlagVariation } from '#utils/flags/records/Variation';

import { RolloutMetricsTable } from './RolloutMetricsTable/RolloutMetricsTable';
import { RolloutStagesTable } from './RolloutStagesTable/RolloutStagesTable';
import type { Metric, Stage } from './types';

import styles from './RolloutInstructionDescription.module.css';

interface Props {
  originalVariationId: string;
  variations: FlagVariation[];
  metrics?: Metric[];
  stages?: Stage[];
  targetVariationId: string;
  rolloutContextKind: string;
  updateDefaultRuleOnCompletion?: boolean;
}

function GridRow({ label, value }: { label: string; value: React.ReactNode }) {
  const labelId = label.toLowerCase().replace(/\s+/g, '-');
  return (
    <Grid layout={GridLayoutValue.FLEX} gap="0">
      <Cell layout={CellLayoutValue.ONE_OF_FOUR}>
        <div className={styles.labelCell} data-test-id={`${labelId}-label`}>
          {label}:
        </div>
      </Cell>
      <Cell layout={CellLayoutValue.THREE_OF_FOUR}>
        <div data-test-id={`${labelId}-value`}>{value}</div>
      </Cell>
    </Grid>
  );
}

export function RolloutInstructionDescription({
  originalVariationId,
  variations,
  metrics,
  stages,
  targetVariationId,
  rolloutContextKind,
  updateDefaultRuleOnCompletion,
}: Props) {
  const originalVariationIndex = getFlagVariationIndexFromId(variations, originalVariationId);
  const targetVariationIndex = getFlagVariationIndexFromId(variations, targetVariationId);

  const totalDuration = stages ? stages.reduce((sum, stage) => sum + (stage.monitoringWindowMilliseconds ?? 0), 0) : 0;
  const formattedTotalDuration = getDisplayDurationFromMs(totalDuration);

  const [stagesVisible, setStagesVisible] = useState(false);
  const [metricsVisible, setMetricsVisible] = useState(false);

  return (
    <Grid direction="column" gap="0" data-test-id="rollout-description">
      <GridRow
        label="Rollout"
        value={
          <span className={styles.rolloutValue}>
            {targetVariationIndex === -1 ? (
              <code>VARIATION NOT FOUND</code>
            ) : (
              <VariationDisplay variation={variations[targetVariationIndex]} variationIndex={targetVariationIndex} />
            )}{' '}
            by <Code>{rolloutContextKind}</Code>
          </span>
        }
      />
      <GridRow
        label="Original variation"
        value={
          originalVariationIndex === -1 ? (
            <code>VARIATION NOT FOUND</code>
          ) : (
            <VariationDisplay variation={variations[originalVariationIndex]} variationIndex={originalVariationIndex} />
          )
        }
      />
      <GridRow
        label="Rollout duration"
        value={
          <span className={styles.rowValue}>
            {formattedTotalDuration}
            {stages && stages.length > 0 && (
              <>
                <TagGroup aria-label="step count">
                  <TagList>
                    <Tag size="small" textValue={`${stages.length} ${stages.length === 1 ? 'step' : 'steps'}`}>
                      {stages.length} {stages.length === 1 ? 'step' : 'steps'}
                    </Tag>
                  </TagList>
                </TagGroup>
                <Button
                  variant="minimal"
                  size="small"
                  className={styles.detailsLink}
                  aria-label={stagesVisible ? 'Hide step details' : 'Show step details'}
                  onPress={() => setStagesVisible((v) => !v)}
                >
                  {stagesVisible ? 'Hide details' : 'Show details'}
                </Button>
              </>
            )}
          </span>
        }
      />
      {stages && stages.length > 0 && stagesVisible && (
        <div className={styles.tableIndent}>
          <RolloutStagesTable stages={stages} />
        </div>
      )}
      {metrics && metrics.length > 0 && (
        <GridRow
          label="Metrics"
          value={
            <span className={styles.rowValue}>
              {metrics.length} {metrics.length === 1 ? 'metric' : 'metrics'}
              <Button
                variant="minimal"
                size="small"
                className={styles.detailsLink}
                aria-label={metricsVisible ? 'Hide metric details' : 'Show metric details'}
                onPress={() => setMetricsVisible((v) => !v)}
              >
                {metricsVisible ? 'Hide details' : 'Show details'}
              </Button>
            </span>
          }
        />
      )}
      {metrics && metrics.length > 0 && metricsVisible && (
        <div className={styles.tableIndent}>
          <RolloutMetricsTable metrics={metrics} rolloutContextKind={rolloutContextKind} />
        </div>
      )}
      {updateDefaultRuleOnCompletion !== undefined && (
        <GridRow
          label="Cleanup phase"
          value={
            updateDefaultRuleOnCompletion ? (
              <span className={styles.rolloutValue}>
                Remove rules and update default rule to serve{' '}
                {targetVariationIndex === -1 ? (
                  <code>VARIATION NOT FOUND</code>
                ) : (
                  <VariationDisplay
                    variation={variations[targetVariationIndex]}
                    variationIndex={targetVariationIndex}
                  />
                )}
              </span>
            ) : (
              'Rules remain in place'
            )
          }
        />
      )}
    </Grid>
  );
}
